<script setup>
import { useQuality } from '@/composables/useQuality'
import { useAbility } from '@casl/vue'
import { useRouter } from 'vue-router'

definePage({
  meta: {
    layout: 'default',
    action: 'view',
    subject: 'Indicator',
  },
})

const ability = useAbility()
const router = useRouter()
const { indicators, loading, saving, fetchIndicators, createIndicator } = useQuality()

onMounted(() => fetchIndicators())

const dialog = ref(false)
const errors = ref({})
const form = ref({ indicator_category_id: null, unit_id: null, name: '', description: '', target_value: null })

const openCreate = () => {
  form.value = { indicator_category_id: null, unit_id: null, name: '', description: '', target_value: null }
  errors.value = {}
  dialog.value = true
}

const save = async () => {
  errors.value = {}
  try {
    await createIndicator(form.value)
    dialog.value = false
    fetchIndicators()
  } catch (e) {
    if (e?.status === 422) errors.value = e?.data?.errors ?? {}
    else console.error('Create indicator error:', e)
  }
}

const headers = [
  { title: 'Nama', key: 'name' },
  { title: 'Kategori', key: 'category' },
  { title: 'Unit', key: 'unit' },
  { title: 'Target', key: 'target_value', align: 'center' },
  { title: 'Nilai Terbaru', key: 'latest_value', align: 'center' },
]
</script>

<template>
  <div>
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">
              Indikator Mutu
            </h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Indikator Area Klinis/Manajerial/Sasaran Keselamatan Pasien (§9.4)
            </p>
          </div>
          <VBtn
            v-if="ability.can('manage', 'Indicator')"
            prepend-icon="ri-add-line"
            @click="openCreate"
          >
            Tambah Indikator
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <VCard>
      <VDataTable
        :headers="headers"
        :items="indicators"
        :loading="loading"
        hide-default-footer
        @click:row="(e, { item }) => router.push({ name: 'quality-indicators-id', params: { id: item.id } })"
      >
        <template #item.category="{ item }">
          {{ item.category?.name ?? '-' }}
        </template>
        <template #item.unit="{ item }">
          {{ item.unit?.name ?? '-' }}
        </template>
        <template #item.target_value="{ item }">
          {{ item.target_value ?? '-' }}
        </template>
        <template #item.latest_value="{ item }">
          <VChip
            v-if="item.latest_value !== null"
            size="small"
            color="primary"
            variant="tonal"
          >
            {{ item.latest_value }} ({{ item.latest_period }})
          </VChip>
          <span
            v-else
            class="text-medium-emphasis"
          >Belum ada data</span>
        </template>
        <template #no-data>
          <div class="text-center py-10">
            <p class="text-medium-emphasis">
              Belum ada indikator
            </p>
          </div>
        </template>
      </VDataTable>
    </VCard>

    <VDialog
      v-model="dialog"
      max-width="560"
      persistent
    >
      <VCard>
        <VCardTitle class="pa-6 pb-0">
          Tambah Indikator
        </VCardTitle>
        <VCardText class="pa-6">
          <VRow>
            <VCol cols="12">
              <MasterDataPicker
                v-model="form.indicator_category_id"
                entity="indicator-categories"
                label="Kategori Indikator *"
                :error-messages="errors.indicator_category_id"
              />
            </VCol>
            <VCol cols="12">
              <MasterDataPicker
                v-model="form.unit_id"
                entity="units"
                label="Unit *"
                :error-messages="errors.unit_id"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="form.name"
                label="Nama Indikator *"
                :error-messages="errors.name"
              />
            </VCol>
            <VCol cols="12">
              <VTextarea
                v-model="form.description"
                label="Deskripsi"
                rows="2"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                v-model="form.target_value"
                type="number"
                label="Target"
                hint="Nilai target bulanan (opsional) — capai target = skor 100"
                persistent-hint
              />
            </VCol>
          </VRow>
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn
            variant="tonal"
            color="secondary"
            :disabled="saving"
            @click="dialog = false"
          >
            Batal
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="save"
          >
            Simpan
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
